import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
} from '@mui/material';
import CheckIcon from '@material-ui/icons/Check';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6b6b6b)';
const BORDER = 'var(--brand-border, #e3e0dc)';

/**
 * Publishing a school / network workspace, in two screens — SNW, SW Parental
 * Consent Flow (5 Oct 2026), slides 41 and 42:
 *
 *   1. ConfirmPublishDialog        — "Publish workspace?"
 *   2. PublishWorkspaceSuccessDialog — the published confirmation
 *
 * There is no parental-consent step any more. The partner used to choose the
 * consent pathway here (and tick an acknowledgement); the school or network now
 * sets up its own method from its own workspace once it is published
 * (ParentalConsent.page.jsx), so publishing only opens the workspace.
 *
 * Every customer-supplied value (`orgName`, `contactEmail`) is rendered as a
 * React child, never through dangerouslySetInnerHTML — a school named
 * `<img onerror=…>` must stay a school named `<img onerror=…>`. This is why
 * `ConfirmationDialog.comp.jsx` was not reused here.
 */

// ─── 1. Confirm publish ──────────────────────────────────────────────────────

const SummaryRow = ({ label, value }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 2,
            py: 1.75,
            borderBottom: `1px solid ${BORDER}`,
        }}
    >
        <Typography sx={{ fontSize: '15px', color: MUTED, flexShrink: 0 }}>{label}</Typography>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, fontFamily: 'monospace', textAlign: 'right', wordBreak: 'break-all' }}>
            {value}
        </Typography>
    </Box>
);

export const ConfirmPublishDialog = ({
    open,
    onClose,
    onConfirm,
    loading = false,
    orgName,
    contactEmail,
}) => (
    <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: 'var(--brand-bg, #FFFBF7)', borderRadius: '20px' } }}
    >
        <DialogContent sx={{ pt: 3.5, px: 3.5, pb: 1 }}>
            <Typography sx={{ fontSize: '24px', fontWeight: 800 }}>
                Publish workspace?
            </Typography>
            <Typography sx={{ fontSize: '15px', color: MUTED, mt: 1.25, mb: 2 }}>
                You are about to open the workspace for {orgName || 'this workspace'}.
            </Typography>

            <Box sx={{ borderTop: `1px solid ${BORDER}` }}>
                <SummaryRow label="Workspace" value={orgName} />
                <SummaryRow label="Workspace admin" value={contactEmail || '—'} />
            </Box>

            <Typography sx={{ fontSize: '14.5px', color: MUTED, mt: 2 }}>
                Once published, the workspace admin will be able to sign in and access the
                assessments, programs, and products you assign.
            </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3.5, pb: 3, pt: 2 }}>
            <Box sx={{ display: 'flex', width: '100%', gap: 1.5 }}>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={15} color="inherit" /> : null}
                    sx={{
                        flex: 1,
                        borderRadius: '999px',
                        py: 1.25,
                        fontWeight: 700,
                        fontSize: '15px',
                        textTransform: 'none',
                        backgroundColor: ORANGE,
                        color: '#fff',
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#c9541e', boxShadow: 'none' },
                    }}
                >
                    {loading ? 'Publishing…' : 'Confirm'}
                </Button>
                <Button
                    onClick={onClose}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                        flex: 1,
                        borderRadius: '999px',
                        py: 1.25,
                        fontWeight: 700,
                        fontSize: '15px',
                        textTransform: 'none',
                        borderColor: BORDER,
                        color: 'var(--brand-text, #1F2328)',
                        backgroundColor: 'var(--brand-surface, #fff)',
                    }}
                >
                    Cancel
                </Button>
            </Box>
        </DialogActions>
    </Dialog>
);

// ─── 2. Published ────────────────────────────────────────────────────────────

/**
 * "Resend activation email" lives here rather than being folded into publish,
 * because the mail can fail while the publish succeeds. When it did fail, the
 * checklist says so instead of claiming an email that never went out.
 */
export const PublishWorkspaceSuccessDialog = ({
    open,
    onClose,
    onResend,
    resending = false,
    orgName,
    contactEmail,
    publishedAt,
    credentialsEmailed = true,
    isSchool = true,
    // Networks publish the schools inside them (PW deck slide 38 — the email
    // goes to "both network and schools"), so the outcome for those schools is
    // reported here rather than happening silently.
    schoolsPublished = 0,
    schoolFailures = [],
}) => {
    const backLabel = isSchool ? 'Back to My Schools' : 'Back to My Clients';

    const stamp = (() => {
        if (!publishedAt) return null;
        const d = new Date(publishedAt);
        if (Number.isNaN(d.getTime())) return null;
        const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        return `Published on ${date} · ${time}`;
    })();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { backgroundColor: 'var(--brand-bg, #FFFBF7)', borderRadius: '20px' } }}
        >
            <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
                <Box
                    sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(46, 125, 50, 0.12)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CheckIcon style={{ fontSize: 28, color: '#2e7d32' }} />
                </Box>

                <Typography sx={{ fontSize: '21px', fontWeight: 800, mt: 2 }}>
                    Workspace Published
                </Typography>

                <Typography sx={{ fontSize: '15px', mt: 1 }}>
                    <strong>{orgName}</strong> is ready to go.
                </Typography>

                <Typography sx={{ fontSize: '13.5px', color: MUTED, mt: 1.25, px: 1 }}>
                    They can now sign in and access the products, assessments, and programs
                    you assigned.
                </Typography>

                {schoolsPublished > 0 && (
                    <Typography sx={{ fontSize: '13.5px', color: MUTED, mt: 1, px: 1 }}>
                        {schoolsPublished === 1
                            ? '1 school in this network was published too, and its contact was emailed.'
                            : `${schoolsPublished} schools in this network were published too, and their contacts were emailed.`}
                    </Typography>
                )}
                {schoolFailures.length > 0 && (
                    <Typography sx={{ fontSize: '13.5px', color: '#b3261e', mt: 1, px: 1 }}>
                        {`Could not publish: ${schoolFailures.join(', ')}. Publish these from their own row.`}
                    </Typography>
                )}

                <Box
                    sx={{
                        border: `1px solid ${BORDER}`,
                        borderRadius: '12px',
                        mt: 2.5,
                        textAlign: 'left',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            padding: '11px 14px',
                            borderBottom: `1px solid ${BORDER}`,
                        }}
                    >
                        <CheckIcon style={{ fontSize: 17, color: '#2e7d32', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '13.5px' }}>Workspace is active</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: '11px 14px' }}>
                        {credentialsEmailed ? (
                            <CheckIcon style={{ fontSize: 17, color: '#2e7d32', flexShrink: 0 }} />
                        ) : (
                            <ErrorOutlineIcon style={{ fontSize: 17, color: '#b26a00', flexShrink: 0 }} />
                        )}
                        <Typography sx={{ fontSize: '13.5px', wordBreak: 'break-all' }}>
                            {credentialsEmailed
                                ? 'Activation email sent to '
                                : 'Activation email could not be sent — try resending below'}
                            {credentialsEmailed && (
                                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                    {contactEmail || 'the account contact'}
                                </Box>
                            )}
                        </Typography>
                    </Box>
                </Box>

                {stamp && (
                    <Typography
                        sx={{ fontSize: '12.5px', color: MUTED, mt: 2, fontFamily: 'monospace' }}
                    >
                        {stamp}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'stretch',
                        justifyContent: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{
                            backgroundColor: ORANGE,
                            color: '#fff',
                            borderRadius: '10px',
                            flex: 1,
                            minWidth: 150,
                            fontWeight: 700,
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#c9541e' },
                        }}
                    >
                        {backLabel}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={onResend}
                        disabled={resending}
                        startIcon={resending ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={{
                            borderRadius: '10px',
                            flex: 1,
                            minWidth: 150,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderColor: BORDER,
                            color: 'var(--brand-text, #333)',
                        }}
                    >
                        {resending ? 'Sending…' : 'Resend activation email'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
